/**
 * Speech Hook
 * Handles OpenAI TTS integration and audio caching
 * IMPORTANT: Preserve exact caching and eviction logic
 */

import { useRef, useCallback, useEffect } from 'react'
import { SPEECH_CONSTANTS, sanitizeOpenAiVoice, sanitizeOpenAiModel } from '@config/speech'
import { API_CONFIG, getApiBaseUrl, getProxyHeaders } from '@config/api'
import { storeTurnstileSessionFromResponse } from '@lib/turnstile'
import { useSpeechStore } from '@stores/speechStore'
import { RateLimitError } from '@lib/speechApi'
import type { OpenAIVoice, OpenAIModel } from '../types'

interface AudioCacheEntry {
  url: string
  createdAt: number
  model: OpenAIModel
  voice: OpenAIVoice
  text: string
}

interface SpeechState {
  audioCache: Map<string, AudioCacheEntry>
  pendingSpeechRequests: Map<string, Promise<AudioCacheEntry | null>>
  cacheRevision: number
  currentAudioCacheKey: string | null
  nextWordProblem: string | null
}

export function useSpeech() {
  const speech = useSpeechStore(state => state.speech)

  const stateRef = useRef<SpeechState>({
    audioCache: new Map(),
    pendingSpeechRequests: new Map(),
    cacheRevision: 0,
    currentAudioCacheKey: null,
    nextWordProblem: null,
  })
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const createSpeechRequestPayload = useCallback(
    ({ text, model, voice }: { text: string; model: OpenAIModel; voice: OpenAIVoice }) => {
      return {
        model: sanitizeOpenAiModel(model),
        voice: sanitizeOpenAiVoice(voice),
        input: String(text ?? ''),
        format: 'mp3',
      }
    },
    []
  )

  const buildSpeechCacheKey = useCallback((text: string, voice: OpenAIVoice, model: OpenAIModel): string | null => {
    const normalizedText = String(text ?? '').trim().toLowerCase()
    if (!normalizedText) {
      return null
    }
    return `${sanitizeOpenAiModel(model)}::${sanitizeOpenAiVoice(voice)}::${normalizedText}`
  }, [])

  const touchSpeechCacheEntry = useCallback((key: string) => {
    const state = stateRef.current
    if (!key || !state.audioCache?.has(key)) {
      return
    }
    const entry = state.audioCache.get(key)!
    state.audioCache.delete(key)
    state.audioCache.set(key, entry)
  }, [])

  const evictOldestSpeechEntry = useCallback(() => {
    const state = stateRef.current
    if (!state.audioCache) {
      state.audioCache = new Map()
      return
    }
    while (state.audioCache.size >= SPEECH_CONSTANTS.SPEECH_CACHE_LIMIT) {
      const oldestKey = state.audioCache.keys().next().value
      if (!oldestKey) {
        return
      }
      if (oldestKey === state.currentAudioCacheKey) {
        const entry = state.audioCache.get(oldestKey)!
        state.audioCache.delete(oldestKey)
        state.audioCache.set(oldestKey, entry)
        continue
      }
      const entry = state.audioCache.get(oldestKey)
      if (entry?.url) {
        URL.revokeObjectURL(entry.url)
      }
      state.audioCache.delete(oldestKey)
    }
  }, [])

  const storeSpeechCacheEntry = useCallback(
    (key: string, entry: AudioCacheEntry) => {
      if (!key || !entry) {
        return
      }
      const state = stateRef.current
      if (!state.audioCache) {
        state.audioCache = new Map()
      }
      if (state.audioCache.has(key)) {
        const existing = state.audioCache.get(key)
        if (existing?.url && existing.url !== entry.url) {
          URL.revokeObjectURL(existing.url)
        }
        state.audioCache.delete(key)
      } else {
        evictOldestSpeechEntry()
      }
      state.audioCache.set(key, entry)
    },
    [evictOldestSpeechEntry]
  )

  const clearSpeechCache = useCallback(() => {
    const state = stateRef.current
    state.cacheRevision = (state.cacheRevision || 0) + 1
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
    state.currentAudioCacheKey = null
    if (state.pendingSpeechRequests) {
      state.pendingSpeechRequests.clear()
    } else {
      state.pendingSpeechRequests = new Map()
    }
    if (state.audioCache) {
      state.audioCache.forEach(entry => {
        if (entry?.url) {
          URL.revokeObjectURL(entry.url)
        }
      })
      state.audioCache.clear()
    } else {
      state.audioCache = new Map()
    }
    state.nextWordProblem = null
  }, [])

  const ensureSpeechCached = useCallback(
    async ({ text, apiKey, model, voice, useProxy }: { text: string; apiKey?: string; model: OpenAIModel; voice: OpenAIVoice; useProxy?: boolean }): Promise<{ key: string | null; entry: AudioCacheEntry | null }> => {
      const state = stateRef.current
      const normalizedText = String(text ?? '').trim()
      if (!normalizedText) {
        return { key: null, entry: null }
      }
      // API key required only in direct mode
      if (!useProxy && !apiKey) {
        return { key: null, entry: null }
      }
      const cacheKey = buildSpeechCacheKey(normalizedText, voice, model)
      if (!cacheKey) {
        return { key: null, entry: null }
      }
      if (!state.audioCache) {
        state.audioCache = new Map()
      }
      if (!state.pendingSpeechRequests) {
        state.pendingSpeechRequests = new Map()
      }
      state.cacheRevision = state.cacheRevision || 0

      const cached = state.audioCache.get(cacheKey)
      if (cached) {
        touchSpeechCacheEntry(cacheKey)
        return { key: cacheKey, entry: cached }
      }

      if (state.pendingSpeechRequests.has(cacheKey)) {
        const entry = await state.pendingSpeechRequests.get(cacheKey)
        if (entry) {
          touchSpeechCacheEntry(cacheKey)
          return { key: cacheKey, entry }
        }
        return { key: cacheKey, entry: null }
      }

      const payload = createSpeechRequestPayload({
        text: normalizedText,
        model,
        voice,
      })
      const revisionAtStart = state.cacheRevision

      // Determine URL and headers based on mode
      const baseUrl = getApiBaseUrl(useProxy ? 'proxy' : 'direct')
      const url = `${baseUrl}${API_CONFIG.TTS_PATH}`
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (useProxy) {
        Object.assign(headers, await getProxyHeaders())
      } else if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`
      }

      const requestPromise = (async (): Promise<AudioCacheEntry | null> => {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        })
        storeTurnstileSessionFromResponse(response)
        if (!response.ok) {
          if (response.status === 429) {
            const retryAfter = parseInt(response.headers.get('Retry-After') || '0', 10)
            const remaining = parseInt(response.headers.get('X-RateLimit-Remaining') || '0', 10)
            const resetTime = parseInt(response.headers.get('X-RateLimit-Reset') || '0', 10)
            throw new RateLimitError(
              `Rate limit exceeded. Try again in ${Math.ceil(retryAfter / 60)} minute(s).`,
              retryAfter,
              remaining,
              resetTime
            )
          }
          throw new Error(`OpenAI request failed with status ${response.status}`)
        }
        const arrayBuffer = await response.arrayBuffer()
        const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' })
        const blobUrl = URL.createObjectURL(blob)
        if ((state.cacheRevision || 0) !== revisionAtStart) {
          URL.revokeObjectURL(blobUrl)
          return null
        }
        const entry: AudioCacheEntry = {
          url: blobUrl,
          createdAt: Date.now(),
          model: sanitizeOpenAiModel(model),
          voice: sanitizeOpenAiVoice(voice),
          text: normalizedText.toLowerCase(),
        }
        storeSpeechCacheEntry(cacheKey, entry)
        return entry
      })()

      state.pendingSpeechRequests.set(cacheKey, requestPromise)
      try {
        const entry = await requestPromise
        if (entry) {
          touchSpeechCacheEntry(cacheKey)
          return { key: cacheKey, entry }
        }
        return { key: null, entry: null }
      } finally {
        state.pendingSpeechRequests.delete(cacheKey)
      }
    },
    [buildSpeechCacheKey, createSpeechRequestPayload, storeSpeechCacheEntry, touchSpeechCacheEntry]
  )

  const stopSpeechPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    audioRef.current = null
    stateRef.current.currentAudioCacheKey = null
  }, [])

  const speakWithOpenAi = useCallback(
    async (text: string) => {
      // Check if enabled: either proxy mode or has API key
      if (!speech?.enabled) {
        throw new Error('OpenAI speech disabled')
      }
      if (!speech.useProxy && !speech.key) {
        throw new Error('Missing API key (not using proxy)')
      }
      const { entry, key } = await ensureSpeechCached({
        text,
        apiKey: speech.key,
        model: speech.model,
        voice: speech.voice,
        useProxy: speech.useProxy,
      })
      if (!entry?.url) {
        throw new Error('No speech entry available')
      }
      stopSpeechPlayback()
      stateRef.current.currentAudioCacheKey = key
      const audio = new Audio(entry.url)
      audioRef.current = audio
      await audio.play()
      return entry
    },
    [ensureSpeechCached, speech, stopSpeechPlayback]
  )

  const warmSpeechCache = useCallback(
    async (text: string) => {
      if (!speech?.enabled) {
        return null
      }
      // Need proxy or API key
      if (!speech.useProxy && !speech.key) {
        return null
      }
      const { entry } = await ensureSpeechCached({
        text,
        apiKey: speech.key,
        model: speech.model,
        voice: speech.voice,
        useProxy: speech.useProxy,
      })
      return entry
    },
    [ensureSpeechCached, speech]
  )

  const isOpenAiSpeechEnabled = useCallback(() => {
    // Enabled if using proxy OR has API key
    return Boolean(speech?.enabled && (speech?.useProxy || speech?.key))
  }, [speech])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSpeechCache()
      stopSpeechPlayback()
    }
  }, [clearSpeechCache, stopSpeechPlayback])

  return {
    ensureSpeechCached,
    clearSpeechCache,
    warmSpeechCache,
    speakWithOpenAi,
    stopSpeechPlayback,
    isOpenAiSpeechEnabled,
    speech,
  }
}
